import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SocialLinks from '../components/SocialLinks';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_fintech-notify-check/artifacts/2hk0onm3_My%20Direct%20debit%20reminder%20app%20logo.png";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-page">
      <header className="glass-header px-4 py-4">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <Link to="/" className="text-text-secondary hover:text-primary transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <img src={LOGO_URL} alt="My Direct Debits" className="h-8 w-8" />
          <span className="font-heading font-semibold text-dark">Terms of Service</span>
        </div>
      </header>

      <main className="px-4 py-8">
        <div className="max-w-3xl mx-auto card-default p-8">
          <h1 className="font-heading font-bold text-dark text-3xl mb-2">Terms of Service</h1>
          <p className="text-text-light text-sm mb-8">Last updated: January 2025</p>

          <div className="prose prose-green max-w-none space-y-6 text-text-secondary">
            <section>
              <h2 className="font-heading font-semibold text-dark text-xl mb-3">1. Acceptance of Terms</h2>
              <p>
                By accessing or using the My Direct Debits application ("Service") operated by Voliciti 
                Group Ventures Ltd ("Company", "we", "us", or "our"), you agree to be bound by these 
                Terms of Service. If you do not agree to these terms, please do not use our Service.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-dark text-xl mb-3">2. Description of Service</h2>
              <p>
                My Direct Debits is a personal finance tracking application that helps users track and 
                manage their direct debit payments. The Service provides reminders and an overview of 
                upcoming payments to help users stay in control of their finances.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-dark text-xl mb-3">3. User Accounts</h2>
              <p>To use our Service, you must:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Be at least 16 years of age</li>
                <li>Provide accurate and complete registration information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Notify us immediately of any unauthorized access</li>
              </ul>
              <p className="mt-2">
                You are responsible for all activities that occur under your account.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-dark text-xl mb-3">4. Free and Premium Services</h2>
              <p>
                Our Service offers both free and premium tiers:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li><strong>Free Tier:</strong> Track up to 3 direct debits</li>
                <li><strong>Premium Tier:</strong> Unlimited direct debits and additional features</li>
              </ul>
              <p className="mt-2">
                Premium subscriptions are billed monthly or annually as selected. You may cancel at any 
                time from within the app or by contacting us.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-dark text-xl mb-3">5. Payment Terms</h2>
              <p>
                Premium subscriptions are processed through Stripe. By subscribing, you authorize us to 
                charge the payment method provided for the subscription fees. Prices are in GBP (£) and 
                may be subject to change with notice.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-dark text-xl mb-3">6. Refund Policy</h2>
              <p>
                We offer refunds on a case-by-case basis. If you are unsatisfied with our Service within 
                the first 14 days of a premium subscription, please contact us to discuss a refund.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-dark text-xl mb-3">7. User Conduct</h2>
              <p>You agree not to:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Use the Service for any illegal purpose</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Interfere with or disrupt the Service</li>
                <li>Use automated means to access the Service without permission</li>
                <li>Violate the rights of other users</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-dark text-xl mb-3">8. Intellectual Property</h2>
              <p>
                The Service and its original content, features, and functionality are owned by Voliciti 
                Group Ventures Ltd and are protected by international copyright, trademark, and other 
                intellectual property laws.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-dark text-xl mb-3">9. Disclaimer</h2>
              <p>
                The Service is provided "as is" without warranties of any kind. We do not guarantee that 
                the Service will be uninterrupted, secure, or error-free. The Service is a tracking tool 
                and does not provide financial advice.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-dark text-xl mb-3">10. Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by law, Voliciti Group Ventures Ltd shall not be liable 
                for any indirect, incidental, special, consequential, or punitive damages resulting from 
                your use of the Service.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-dark text-xl mb-3">11. Account Termination</h2>
              <p>
                You may delete your account at any time from within the app. We reserve the right to 
                suspend or terminate accounts that violate these Terms of Service.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-dark text-xl mb-3">12. Changes to Terms</h2>
              <p>
                We reserve the right to modify these Terms at any time. We will notify users of 
                significant changes via email or in-app notification. Continued use of the Service 
                after changes constitutes acceptance of the new Terms.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-dark text-xl mb-3">13. Governing Law</h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of England 
                and Wales, without regard to conflict of law principles.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-dark text-xl mb-3">14. Contact Information</h2>
              <p>
                For questions about these Terms, please contact us at:
              </p>
              <p className="mt-2">
                <strong>Voliciti Group Ventures Ltd</strong><br />
                Email: info@mddreminder.uk
              </p>
            </section>
          </div>
        </div>
      </main>

      <footer className="px-4 py-6 text-center">
        <div className="mb-3">
          <SocialLinks />
        </div>
        <p className="text-text-light text-sm">
          © 2025 Voliciti Group Ventures Ltd. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
